import { gql, useMutation } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'
import { usePrevious } from 'react-use'

import useBrands from 'pared/layouts/hooks/useBrands'

import { IQueryDataType } from './useListLocationGroup'

export interface IOptionType {
  manageType: 'editLocationGroup'
  locationGroupId: number
  locationGroupType: string
  locationGroupName: string
  locationGroupEmail: string
  locationIds: number[]
}

export interface IDataType {
  locationGroupType: string
  value: string
  onChange: (value: IDataType['value']) => void
  email: string
  onChangeEmail: (email: IDataType['email']) => void
  loading: boolean
  errorMessage?: string
  submit: () => Promise<void>
  deletable: boolean
  delete: () => Promise<void>
}

const updateMutate = gql`
  mutation UpdateLocationGroup(
    $locationGroupId: Int!
    $name: String!
    $email: String
  ) {
    updateLocationGroup(
      input: { id: $locationGroupId, patch: { name: $name, email: $email } }
    ) {
      clientMutationId
    }
  }
`

const deleteMutate = gql`
  mutation DeleteLocationGroup($locationGroupId: Int!) {
    deleteLocationGroupV2(input: { iLocationGroupId: $locationGroupId }) {
      clientMutationId
    }
  }
`

const useEditLocationGroup = (
  data: IQueryDataType | null,
  option: IOptionType | null,
  close: () => void,
) => {
  const editLocationGroupHook = useState<string>('')
  const editEmailHook = useState<string>('')
  const isSubmittingHook = useState(false)
  const prevManageType = usePrevious(option?.manageType)
  const { brand } = useBrands()
  const [updateLocationGroup, { loading: updateLoading }] = useMutation(
    updateMutate,
    {
      refetchQueries: ['listLocationGroupMapping', 'locationGroups'],
      awaitRefetchQueries: true,
    },
  )
  const [deleteLocationGroup, { loading: deleteLoading }] = useMutation(
    deleteMutate,
    {
      refetchQueries: ['listLocationGroupMapping', 'locationGroups'],
      awaitRefetchQueries: true,
    },
  )
  const loading = updateLoading || deleteLoading
  const options = useMemo(
    () =>
      !option
        ? []
        : (data?.locationGroups.nodes || [])
            .filter((n) => !n.disable && n.type === option.locationGroupType)
            .map((n) => n.name),
    [data, option],
  )

  useEffect(() => {
    if (prevManageType !== option?.manageType) {
      if (option?.manageType === 'editLocationGroup') {
        editLocationGroupHook[1](option.locationGroupName)
        editEmailHook[1](option.locationGroupEmail)
      } else {
        editLocationGroupHook[1]('')
        editEmailHook[1]('')
        isSubmittingHook[1](false)
      }
    }
  }, [option, prevManageType, editLocationGroupHook, isSubmittingHook])

  return useMemo((): IDataType | undefined => {
    if (option?.manageType !== 'editLocationGroup') return

    const [value, onChange] = editLocationGroupHook
    const [email, onChangeEmail] = editEmailHook
    const [isSubmitting, setIsSubmitting] = isSubmittingHook
    const validator = (isSubmitting: boolean) => {
      if (isSubmitting) {
        if (!value) return `Couldn't be empty`

        if (
          value === option.locationGroupName &&
          email === option.locationGroupEmail
        )
          return `No change has been made`
      }

      if (value !== option.locationGroupName && options.includes(value))
        return `${value} exists`
    }

    return {
      locationGroupType: option.locationGroupType,
      value,
      onChange,
      email,
      onChangeEmail,
      loading,
      errorMessage: validator(isSubmitting),
      submit: async () => {
        const errorMessage = validator(true)
        const hasError = Boolean(errorMessage)

        if (!isSubmitting) setIsSubmitting(true)

        if (loading || hasError) return

        await updateLocationGroup({
          variables: {
            locationGroupId: option.locationGroupId,
            name: value,
            email: (email || '').trim().toLowerCase() || null,
          },
        })
        setIsSubmitting(false)
        close()
      },
      deletable: option.locationIds.length === 0,
      delete: async () => {
        await deleteLocationGroup({
          variables: {
            locationGroupId: option.locationGroupId,
          },
        })
        close()
      },
    }
  }, [
    option,
    close,
    editLocationGroupHook,
    isSubmittingHook,
    loading,
    brand,
    options,
  ])
}

export default useEditLocationGroup
